import { pathLabelOf, shortUuid, type Container } from "@egalregal/shared";
import { getMirroredPlacements } from "../search/catalogStore";

/** One place a scanned item sits, ready to render and adjust. */
export interface PlacementOption {
  containerId: string;
  /** The container's display name (short id when it is not loaded locally). */
  name: string;
  /** Root-to-container breadcrumb, or the name when the path is unknown. */
  path: string;
  /** Absolute quantity currently recorded for this placement. */
  quantity: number;
}

/**
 * Placements of a mirrored item. The browser mirror is the offline source of
 * truth (ADR-0026) and every placement carries its container id, so a placement
 * whose container is not in the loaded slice still shows up under its short id
 * and the user can always choose it.
 *
 * @param containers the locally loaded containers (for names and paths).
 * @param itemId the scanned item's id.
 * @returns positive placements sorted by their display path.
 */
export function placementOptionsFor(
  containers: readonly Container[],
  itemId: string,
): PlacementOption[] {
  return getMirroredPlacements()
    .filter((p) => p.itemId === itemId && p.quantity > 0)
    .map((p) => {
      const container = containers.find((c) => c.id === p.containerId);
      const name = container?.name ?? shortUuid(p.containerId);
      const path = container ? pathLabelOf(containers, container.id) : name;
      return { containerId: p.containerId, name, path, quantity: p.quantity };
    })
    .sort((a, b) => a.path.localeCompare(b.path));
}
